import { AlertTriangle, CheckCircle2, Info, type LucideIcon, X, XCircle } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';

import { cn } from '../../lib/cn';
import { usePrefersReducedMotion } from '../../lib/use-prefers-reduced-motion';
import { type ToastItem, type ToastTone, useToastStore } from './toast-store';

const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-emerald-200 text-emerald-900 [&_svg]:text-emerald-600',
  error: 'border-red-200 text-red-900 [&_svg]:text-red-600',
  info: 'border-sky-200 text-sky-900 [&_svg]:text-sky-600',
  warning: 'border-amber-200 text-amber-900 [&_svg]:text-amber-600',
};

/**
 * Mounted once, at the same level as the router (`App.tsx`), so any screen
 * can call `useToast()` without threading a provider through the tree.
 *
 * Does not use `useDismissableOverlay`: a toast never traps or moves focus
 * (scenario "Toast se anuncia sin robar el foco") \u2014 the `aria-live` region
 * is what announces it.
 */
export function ToastViewport(): JSX.Element {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }): JSX.Element {
  const Icon = TONE_ICON[toast.tone];
  const urgent = toast.tone === 'error';
  const prefersReducedMotion = usePrefersReducedMotion();
  const [entered, setEntered] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [prefersReducedMotion]);

  return (
    <div
      role={urgent ? 'alert' : 'status'}
      aria-live={urgent ? 'assertive' : 'polite'}
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-white p-4 shadow-lg transition-all duration-200 ease-out',
        TONE_STYLES[toast.tone],
        entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <p className="flex-1 text-sm">{toast.message}</p>
      <button
        type="button"
        aria-label="Cerrar notificación"
        onClick={onDismiss}
        className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
